<template>
  <div ref="benchmarksCard">
    <div v-if="showMessage" class="benchmarks-card-content" :class="messageClasses">
      <div class="title-content">
        <BenchmarksCardTitle :title="title"/>
      </div>
      <BenchmarksMessage :shouldBlurValues="shouldBlurValues" :dataError="error" :title="title"/>
    </div>
    <div v-else class="benchmarks-card-content">
      <div class="chart-content">
        <div class="title-content">
          <BenchmarksCardTitle :title="title"/>
          <Legend
            :labels="legendLabels"
            :dotColors="legendDotColors"
          />
        </div>
        <Loader
          v-if="loading"
          class="loader"
          :loading="loading"
        />
        <BenchmarksChart
          v-else
          :data="data"
          :width="chartWidth"
          :height="chartHeight"
          :theme="theme"
          :dimension="dimension"
          :hasTransitions="true"
          :labels="labels"
          :isInPercentages="isInPercentages"
          :useShortLabel="useShortLabel"
        >
        </BenchmarksChart>
      </div>
      <div
        class="table-content"
        id="benchmarks-small-table"
        :style="{ width: chartWidth + 'px' }">
        <BenchmarksTable
          :data="rows"
          :columns="columns"
          :loading="loading"
          :sortKey="sortKey"
          :sortOrder="sortOrder"
          :items-per-page="rows.length"
          :isInPercentages="isInPercentages"
          v-on="$listeners"
        />
        <div v-if="!loading" id="link-container">
          <Anchor @click="$emit('expand')">
            View More
            <template v-slot:icon>
              <ArrowForwardIcon/>
            </template>
          </Anchor>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BenchmarksChart from '../../../Benchmarks/BenchmarksChart/BenchmarksChart.vue';
import BenchmarksCardTitle from '../BenchmarksCardTitle/BenchmarksCardTitle.vue';
import Legend from '../../../../common/Legend.vue';
import BenchmarksMessage
  from '../../../Benchmarks/BenchmarksMessage/BenchmarksMessage.vue';
import BenchmarksTable from '../../BenchmarksTable.vue';
import { Anchor, ArrowForwardIcon } from '@mikmak/components';
import Loader from '../../../Common/Charting/Shared/Loader.vue';

const defaultChartWidth = 624;
const defaultChartHeight = 271;

export default {
  props: {
    theme: {
      type: String,
      default: 'category',
      validator: v => {
        return ['category', 'brand'].indexOf(v) > -1;
      },
    },
    shouldBlurValues: {
      type: Boolean,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Array,
      required: true,
    },
    error: {
      type: Boolean,
    },
    labels: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    dimension: {
      type: String,
    },
    isInPercentages: {
      type: Boolean,
      default: true,
    },
    rows: {
      type: Array,
      required: true,
    },
    columns: {
      type: Array,
      required: true,
    },
    sortKey: {
      type: String,
    },
    sortOrder: {
      type: Number,
    },
    legendLabels: {
      type: Array,
      required: true,
    },
    legendDotColors: {
      type: Object,
      required: true,
    },
  },
  components: {
    BenchmarksChart,
    BenchmarksCardTitle,
    Legend,
    Anchor,
    ArrowForwardIcon,
    BenchmarksTable,
    BenchmarksMessage,
    Loader,
  },
  data() {
    return {
      chartWidth: defaultChartWidth,
      chartHeight: defaultChartHeight,
      defaultMargin: 32,
    };
  },
  methods: {
    handleResize() {
      const benchmarksCard = this.$refs.benchmarksCard;

      const innerWidth = benchmarksCard.offsetWidth - this.defaultMargin;
      const defaultChartWidth = innerWidth/2 - this.defaultMargin/2;

      this.chartWidth = innerWidth > 1280 && !this.showStackedView
        ? defaultChartWidth
        : innerWidth;
    },
  },
  computed: {
    showStackedView() {
      return this.legendLabels.length > 2;
    },
    showMessage() {
      return this.shouldBlurValues || this.error && !this.loading;
    },
    messageClasses() {
      return {
        blur: this.shouldBlurValues,
        error: this.error,
      };
    },
    useShortLabel() {
      return !this.showStackedView && this.data.length > 3;
    },
  },
  created() {
    window.addEventListener('resize', this.handleResize);
  },
  destroyed() {
    window.removeEventListener('resize', this.handleResize);
  },
  mounted() {
    this.handleResize();
  },
};
</script>

<style>
.benchmarks-card-container .chart-block {
  margin: 5px 0px;
}

#link-container .link {
  flex-direction: row-reverse
}

#link-container .link-icon {
  margin-right: 0;
  margin-left: 10px;
}
</style>

<style scoped>
.benchmarks-card-content {
  padding: 16px 16px 8px 16px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  align-content: space-between;
  flex-wrap: wrap;
}

.title-content {
  display: flex;
  height: 47px;
  justify-content: space-between;
}

.table-content {
  text-align: right;
  position: relative;
  height: 325px;
}

#link-container {
  position: absolute;
  bottom: 0;
  right: 0;
}

.blur {
  height: 350px;
  position: relative;
  background-image: url("../../../../../assets/benchmarks-card-blur.png");
}

.error {
  height: 350px;
  position: relative;
  display: block;
}

.loader {
  width: 624px;
  height: 271px;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
